import React, { memo } from 'react';
import { Alert, CircularProgress, Stack, Typography } from 'investira.react.components';
import { displays } from 'investira.react.lib';
import { formats, validators } from 'investira.sdk';
import {
  ATIVO_MERCADO,
  FUNDO_DISTRIBUICAO,
  FUNDO_ESTRATEGIA,
  PUBLICO_ALVO
} from '@investirapri/financa.js/lib/enums';
import { INDICADORES, SETORES } from '../../../enums';
import FUNDO_SITUACAO from '../../../enums/FUNDO_SITUACAO';
import { InlineInfo, PessoaImage } from '../../../components/molecules';
import helpers from '../../../utils/helpers';

/**
 * Exibe os detalhes do fundo em foco e os avisos relacionados à seleção.
 */
const ComparadorFundosFocusedItem = memo(
  ({ focusedItem, isSelected, maxSelected, reachedLimit }) => {
    const xRaw = focusedItem?.raw || {};

    /** Formata um valor numérico conforme o padrão visual do app. */
    function pvFormatNumber(pValue, pCurrency = false, pDecimal = 2) {
      return formats.formatNumber(pValue, pDecimal, true, pCurrency);
    }

    /** Formata um valor percentual preservando o valor zero. */
    function pvFormatPercent(pValue) {
      return `${pvFormatNumber(pValue)}%`;
    }

    /** Retorna Sim ou Não para um atributo booleano. */
    function pvFormatBoolean(pValue) {
      return pValue ? 'Sim' : 'Não';
    }

    /** Localiza a descrição de uma opção de domínio pelo identificador. */
    function pvGetOptionDescription(pOptions, pKey, pValue, pDescriptionKey = 'descricao') {
      const xOption = Object.values(pOptions).find(
        pOption => String(pOption[pKey]) === String(pValue)
      );

      return xOption?.[pDescriptionKey] || pValue;
    }

    /**
     * Retorna a descrição das estratégias do fundo a partir do identificador bitwise.
     * @param {number} pEstrategiaId - Soma bitwise dos ids das estratégias.
     * @returns {string} Descrições das estratégias separadas por vírgula, ou vazio se nenhum correspondido.
     */
    function pvGetFundoEstrategiaDesc(pEstrategiaId) {
      if (!validators.isNumber(pEstrategiaId) || !pEstrategiaId) {
        return '';
      }

      const xDescs = [];
      for (const xKey in FUNDO_ESTRATEGIA) {
        if (Object.prototype.hasOwnProperty.call(FUNDO_ESTRATEGIA, xKey)) {
          const xObj = FUNDO_ESTRATEGIA[xKey];
          // Checa se o bit correspondente está presente no valor passado
          if ((pEstrategiaId & xObj.id) === xObj.id) {
            xDescs.push(xObj.descricao);
          }
        }
      }
      return xDescs.join(', ');
    }

    const xFilterAttributes = [
      ['CNPJ', focusedItem?.cnpj ? displays.cnpj(focusedItem.cnpj) : null],
      ['Gestora', focusedItem?.gestor || null],
      ['ISIN', focusedItem?.isin || null],
      ['Série', focusedItem?.serie || null],
      ['Classe', focusedItem?.classe || null],
      [
        'Benchmark',
        focusedItem?.benchmarkIndicadorId
          ? INDICADORES[focusedItem.benchmarkIndicadorId].indicador_sigla
          : null
      ],
      ['Tipo de ativo', focusedItem?.ativoTipo || null],
      ['ESG', !validators.isNull(focusedItem?.esg) ? pvFormatBoolean(focusedItem.esg) : null],
      [
        'Exclusivo',
        !validators.isNull(focusedItem?.exclusivo) ? pvFormatBoolean(focusedItem.exclusivo) : null
      ],
      [
        'Fechado',
        !validators.isNull(focusedItem?.fechado) ? pvFormatBoolean(focusedItem.fechado) : null
      ],
      [
        'Estratégia',
        focusedItem?.fundoEstrategiaId
          ? pvGetFundoEstrategiaDesc(focusedItem.fundoEstrategiaId)
          : null
      ],
      ['Mercado', focusedItem?.mercado ? ATIVO_MERCADO[focusedItem.mercado].descricao : null],
      [
        'Setor',
        xRaw.top_setor_id
          ? pvGetOptionDescription(SETORES, 'setor_id', xRaw.top_setor_id, 'setor')
          : null
      ],
      [
        'Situação',
        xRaw.situacao ? pvGetOptionDescription(FUNDO_SITUACAO, 'value', xRaw.situacao) : null
      ],
      [
        'Tempo de atuação',
        !validators.isNull(xRaw.criado_a_mais_de)
          ? `${pvFormatNumber(xRaw.criado_a_mais_de)} anos`
          : null
      ],
      ['CVM 175', !validators.isNull(xRaw.cvm_175) ? pvFormatBoolean(xRaw.cvm_175) : null],
      ['Longo prazo', !validators.isNull(xRaw.longo) ? pvFormatBoolean(xRaw.longo) : null],
      ['Fundo de cotas', !validators.isNull(xRaw.cotas) ? pvFormatBoolean(xRaw.cotas) : null],
      [
        'Taxa de administração',
        !validators.isNull(xRaw.taxa_adm) ? pvFormatPercent(xRaw.taxa_adm) : null
      ],
      [
        'Não padronizado',
        !validators.isNull(xRaw.nao_padronizado) ? pvFormatBoolean(xRaw.nao_padronizado) : null
      ],
      [
        'Condomínio',
        !validators.isNull(xRaw.condominio_fechado)
          ? xRaw.condominio_fechado
            ? 'Fechado'
            : 'Aberto'
          : null
      ],
      [
        'Responsabilidade limitada',
        !validators.isNull(xRaw.responsabilidade_limitada)
          ? pvFormatBoolean(xRaw.responsabilidade_limitada)
          : null
      ],
      [
        'Qualificado',
        !validators.isNull(xRaw.qualificado) ? pvFormatBoolean(xRaw.qualificado) : null
      ],
      [
        'Profissional',
        !validators.isNull(xRaw.profissional) ? pvFormatBoolean(xRaw.profissional) : null
      ],
      [
        'Público-alvo',
        !validators.isNull(xRaw.publico_alvo)
          ? pvGetOptionDescription(PUBLICO_ALVO, 'id', xRaw.publico_alvo)
          : null
      ],
      [
        'Distribuição',
        !validators.isNull(xRaw.distribuicao)
          ? pvGetOptionDescription(FUNDO_DISTRIBUICAO, 'id', xRaw.distribuicao)
          : null
      ],
      [
        'Aplicação mínima',
        !validators.isNull(xRaw.aplicacao_min) ? pvFormatNumber(xRaw.aplicacao_min, true) : null
      ],
      [
        'Prazo para Resgate',
        !validators.isNull(xRaw.resgate_carencia)
          ? `${pvFormatNumber(xRaw.resgate_carencia)} dias`
          : null
      ],
      [
        'Previdenciário',
        !validators.isNull(xRaw.previdenciario) ? pvFormatBoolean(xRaw.previdenciario) : null
      ],
      [
        'Estruturado',
        !validators.isNull(xRaw.estruturado) ? pvFormatBoolean(xRaw.estruturado) : null
      ],
      ['Retorno', !validators.isNull(xRaw.retorno) ? pvFormatNumber(xRaw.retorno, false, 3) : null],
      [
        'Dias acima do benchmark',
        !validators.isNull(xRaw.perc_dias_acima_benchmark)
          ? pvFormatPercent(xRaw.perc_dias_acima_benchmark)
          : null
      ],
      [
        'Meses acima do benchmark',
        !validators.isNull(xRaw.perc_meses_acima_benchmark)
          ? pvFormatPercent(xRaw.perc_meses_acima_benchmark)
          : null
      ],
      ['Sharpe', !validators.isNull(xRaw.sharpe) ? pvFormatNumber(xRaw.sharpe) : null],
      [
        'Tracking error',
        !validators.isNull(xRaw.tracking_error) ? pvFormatPercent(xRaw.tracking_error) : null
      ],
      [
        'Volatilidade',
        !validators.isNull(xRaw.volatilidade) ? pvFormatPercent(xRaw.volatilidade) : null
      ],
      ['Patrimônio líquido', !validators.isNull(xRaw.pl) ? pvFormatNumber(xRaw.pl, true) : null],
      [
        'Cotistas',
        !validators.isNull(xRaw.cotistas) ? formats.formatNumber(xRaw.cotistas, 0, true) : null
      ],
      [
        'PL do gestor',
        !validators.isNull(xRaw.gestor_pl) ? pvFormatNumber(xRaw.gestor_pl, true) : null
      ],
      [
        'Cotistas do gestor',
        !validators.isNull(xRaw.gestor_cotistas)
          ? formats.formatNumber(xRaw.gestor_cotistas, 0, true)
          : null
      ],
      [
        'PL do administrador',
        !validators.isNull(xRaw.adm_pl) ? pvFormatNumber(xRaw.adm_pl, true) : null
      ],
      [
        'Cotistas do administrador',
        !validators.isNull(xRaw.adm_cotistas)
          ? formats.formatNumber(xRaw.adm_cotistas, 0, true)
          : null
      ]
    ];

    return (
      <Stack
        spacing={1}
        aria-live="polite"
        sx={{
          flex: 1,
          minWidth: 0,
          maxHeight: { xs: 700, md: '100%' },
          overflowY: 'auto'
        }}>
        {focusedItem ? (
          <>
            {reachedLimit ? (
              <Alert severity="warning">Você já selecionou {maxSelected} fundos.</Alert>
            ) : null}

            <Stack gap={2}>
              {focusedItem.logoPessoaId ? (
                <PessoaImage
                  logoPessoaId={focusedItem.logoPessoaId}
                  alt={focusedItem.pessoa}
                  hasLogo={focusedItem.logo_ok}
                  size={32}
                />
              ) : null}
              <Stack>
                <Typography variant="subtitle2">
                  {helpers.getAtivoDescricao(focusedItem)}
                </Typography>
                {focusedItem.ativo ? (
                  <Typography variant="caption" color="textSecondary">
                    {focusedItem.pessoa}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
            {isSelected ? (
              <Alert severity="success" sx={{ mb: 2, py: 0.5 }}>
                Este fundo já está selecionado.
              </Alert>
            ) : null}
            <Stack py={2} sx={{ gap: 1 }}>
              {xFilterAttributes.map(([pLabel, pValue]) =>
                !validators.isNull(pValue) && pValue !== '' ? (
                  <InlineInfo key={pLabel} label={pLabel} value={pValue} />
                ) : null
              )}
            </Stack>
          </>
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, minHeight: 80 }}>
            <CircularProgress size={32} />
          </Stack>
        )}
      </Stack>
    );
  }
);

export default ComparadorFundosFocusedItem;
